import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Link, List, ListItemButton, ListItemText, Radio, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';

import apiClient from '@src/api/clients';
import {
  claimSparePage, createFacebookPage, listFacebookPages, listSpareChoices, updateFacebookPage,
} from '@src/api/facebookPages';
import { toast } from '@src/components/core/toaster';

const STATUS_CHIPS = {
  done: { label: 'Done', color: 'success' },
  waiting_on_client: { label: 'Waiting on client', color: 'warning' },
  waiting_on_step: { label: 'Waiting', color: 'default' },
  waiting_on_staff: { label: 'Your move', color: 'warning', variant: 'outlined' },
  ready: { label: 'Ready', color: 'info' },
  ran: { label: 'Started', color: 'info' },
  report_only: { label: 'Report only', color: 'default', variant: 'outlined' },
  failed: { label: 'Failed', color: 'error' },
  skipped: { label: 'Not needed', color: 'default', variant: 'outlined' },
};

const MODE_NOTE = {
  off: 'Runner is off — this shows what each step is waiting on; nothing runs.',
  report: 'Runner is in report mode — it records what would run and calls nothing.',
  apply: 'Runner is on — ready steps run every 20 minutes (each step’s own setting still applies).',
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

// The launch runner's steps for this client: brand profile through the paused
// campaign build. Staff only; "Run now" retries anything that failed.
export default function LaunchStatusCard({ clientSlug }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pageDialog, setPageDialog] = useState(null); // 'spare' | 'own' | null
  const url = `app1/clients/${clientSlug}/launch-status/`;

  const load = useCallback(() => {
    if (!clientSlug) return;
    apiClient
      .request({ url, method: 'GET' })
      .then((res) => setData(res?.data || { steps: [] }))
      .catch(() => setData({ steps: [], error: true }));
  }, [clientSlug, url]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await apiClient.request({ url, method: 'POST', payload: {} });
      toast.success(res?.data?.detail || 'Queued');
      if (res?.data?.steps) setData(res.data);
      setTimeout(load, 15000);
    } catch (err) {
      const body = err?.response?.data;
      toast.error(body?.detail || 'Could not start the runner');
      if (body?.steps) setData(body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Launch steps</Typography>
          <Button size="small" variant="outlined" onClick={runNow} disabled={busy || !data || data.mode === 'off'}>
            Run now
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          {data ? MODE_NOTE[data.mode] || '' : ''}
        </Typography>
        {data === null && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
        {data?.error && <Typography variant="body2" color="text.secondary">Could not load launch status.</Typography>}
        <Stack spacing={1}>
          {(data?.steps || []).map((s) => {
            const chip = STATUS_CHIPS[s.status] || { label: s.status, color: 'default' };
            const tip = [
              s.at && `Updated ${fmtDate(s.at)}`,
              s.attempts > 0 && `${s.attempts} failed attempt${s.attempts === 1 ? '' : 's'}`,
              s.next_attempt_at && `Next try ${fmtDate(s.next_attempt_at)}`,
              s.escalated_at && `Team told ${fmtDate(s.escalated_at)}`,
            ].filter(Boolean).join(' · ');
            return (
              <Box key={s.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>{s.label}</Typography>
                  {s.message && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                      {s.message}
                    </Typography>
                  )}
                  {s.key === 'facebook_page' && !['done', 'skipped'].includes(s.status) && data?.client_id && (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Button size="small" variant="outlined" onClick={() => setPageDialog('spare')}>
                        Pick a spare Page
                      </Button>
                      <Button size="small" onClick={() => setPageDialog('own')}>Link existing Page</Button>
                    </Stack>
                  )}
                </Box>
                <Tooltip title={tip} disableHoverListener={!tip}>
                  <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant || 'filled'} />
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
      {pageDialog && data?.client_id && (
        <PageStepDialog
          clientId={data.client_id}
          initialTab={pageDialog}
          onClose={() => setPageDialog(null)}
          onDone={() => { setPageDialog(null); load(); }}
        />
      )}
    </Card>
  );
}

/**
 * The Facebook page step, done by a person: give the client one of our spare
 * Pages (renamed and set up from their record) or link a Page they already
 * have — one we can see, or one added by its Page ID.
 */
function PageStepDialog({ clientId, initialTab, onClose, onDone }) {
  const [tab, setTab] = useState(initialTab);
  const [spares, setSpares] = useState(null);
  const [mode, setMode] = useState('');
  const [unassigned, setUnassigned] = useState(null);
  const [picked, setPicked] = useState(null);
  const [search, setSearch] = useState('');
  const [manual, setManual] = useState({ page_id: '', page_name: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPicked(null);
    if (tab === 'spare' && spares === null) {
      listSpareChoices(clientId)
        .then(({ data }) => { setSpares(data?.pages || []); setMode(data?.mode || ''); })
        .catch(() => setSpares([]));
    }
    if (tab === 'own' && unassigned === null) {
      listFacebookPages({ client: 'unassigned' })
        .then(({ data }) => setUnassigned(Array.isArray(data) ? data : data?.results || []))
        .catch(() => setUnassigned([]));
    }
  }, [tab, clientId, spares, unassigned]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = unassigned || [];
    return (q ? rows.filter((p) => `${p.page_name} ${p.page_id}`.toLowerCase().includes(q)) : rows).slice(0, 50);
  }, [unassigned, search]);

  const fail = (err, fallback) => {
    const body = err?.response?.data || {};
    toast.error(body.detail || Object.values(body).flat().join(' ') || fallback);
  };

  const claim = async () => {
    setSaving(true);
    try {
      const { data } = await claimSparePage(clientId, picked);
      if (data?.status === 'claimed') {
        toast.success(data.needs_rename
          ? `Page claimed — rename it to "${data.setup?.target_name}" in Facebook`
          : 'Page claimed and set up');
        onDone();
      } else if (data?.status === 'would_claim') {
        toast.info('Report mode — nothing was changed (FACEBOOK_PAGE_AUTOSETUP_MODE=report).');
      } else if (data?.status === 'has_page' || data?.status === 'exists') {
        toast.info('This client already has a Page.');
        onDone();
      } else {
        toast.error(`Could not claim the Page (${data?.status || 'unknown'}).`);
      }
    } catch (err) {
      fail(err, 'Could not claim the Page');
    } finally {
      setSaving(false);
    }
  };

  const link = async () => {
    setSaving(true);
    try {
      if (picked) {
        await updateFacebookPage(picked, { client: clientId });
      } else {
        await createFacebookPage({ ...manual, client: clientId });
      }
      toast.success('Page linked');
      onDone();
    } catch (err) {
      fail(err, 'Could not link the Page');
    } finally {
      setSaving(false);
    }
  };

  const canLink = picked || (manual.page_id.trim() && manual.page_name.trim());

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Facebook page</DialogTitle>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 3 }}>
        <Tab value="spare" label="Spare Page" />
        <Tab value="own" label="Client's own Page" />
      </Tabs>
      <DialogContent dividers sx={{ minHeight: 280 }}>
        {tab === 'spare' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The Page is given to this client and its picture, about, email and website are set from their
              record. Facebook doesn&apos;t let us change the name — you&apos;ll rename it there.
            </Typography>
            {mode === 'off' && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                Spare Page setup is off (FACEBOOK_PAGE_AUTOSETUP_MODE) — turn it on to claim.
              </Typography>
            )}
            {spares === null && <CircularProgress size={20} />}
            {spares?.length === 0 && <Typography variant="body2">No spare Pages are free.</Typography>}
            <List dense disablePadding>
              {(spares || []).map((p) => (
                <ListItemButton key={p.id} selected={picked === p.id} onClick={() => setPicked(p.id)}>
                  <Radio size="small" checked={picked === p.id} sx={{ mr: 1 }} />
                  <ListItemText
                    primary={p.page_name}
                    secondary={`${p.why}${p.needs_rename ? ' · rename needed' : ''}`}
                  />
                  <Link href={p.facebook_url} target="_blank" rel="noopener" variant="caption"
                    onClick={(e) => e.stopPropagation()}>
                    View
                  </Link>
                </ListItemButton>
              ))}
            </List>
          </>
        )}
        {tab === 'own' && (
          <>
            <TextField size="small" fullWidth placeholder="Search Pages with no client" value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }} />
            {unassigned === null && <CircularProgress size={20} />}
            <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
              {shown.map((p) => (
                <ListItemButton key={p.id} selected={picked === p.id}
                  onClick={() => setPicked(picked === p.id ? null : p.id)}>
                  <Radio size="small" checked={picked === p.id} sx={{ mr: 1 }} />
                  <ListItemText primary={p.page_name} secondary={p.page_id} />
                </ListItemButton>
              ))}
            </List>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 1 }}>
              Not listed? Add it by its Page ID (the long number in the Page&apos;s About section).
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Page ID" value={manual.page_id} disabled={Boolean(picked)}
                onChange={(e) => setManual((m) => ({ ...m, page_id: e.target.value }))} />
              <TextField size="small" label="Page name" value={manual.page_name} disabled={Boolean(picked)}
                onChange={(e) => setManual((m) => ({ ...m, page_name: e.target.value }))} sx={{ flex: 1 }} />
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {tab === 'spare' ? (
          <Button variant="contained" onClick={claim} disabled={saving || !picked || mode === 'off'}>
            Claim Page
          </Button>
        ) : (
          <Button variant="contained" onClick={link} disabled={saving || !canLink}>Link Page</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

PageStepDialog.propTypes = {
  clientId: PropTypes.number.isRequired,
  initialTab: PropTypes.oneOf(['spare', 'own']).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

LaunchStatusCard.propTypes = {
  clientSlug: PropTypes.string,
};
